// The boat's home (TOM-510, cloud ADR 0335; the design handoff in
// docs/design/design_handoff_neo_home, with the owner's answers in its
// decisions.md). The boat opens here: one board, the one for what the boat is
// doing now, under a fixed header.
//
// - The header doesn't scroll. In the Marina look (cloud ADR 0396 §6,
//   TOM-677) it sits over a decorative marina scene (the day picture in Light,
//   the same picture darkened in Dark, none in Night) under a scrim, or the
//   boat's own photo when an owner set one in Settings (TOM-683, ADR 0397),
//   across the banner with the header as tall as it. It holds the boat
//   and its presence, the board's name, since when the boat has been in its
//   mode (from its mode history, ADR 0321), and at the top right Neo's
//   headline as a glass pill (TOM-526, where the quick readings were), the
//   weather when the boat has a forecast, and the time on the boat's clock.
//   Home has no Edit board pencil (Mike, 2026-10-16): a board is edited from
//   its own page. Only the area under it scrolls, over the scene blurred as a
//   Dark card's glass needs.
// - The board is always the mode's (TOM-526): home has no board menu, and other
//   boards open from the rail's and the sidebar's boards. The board's name
//   opens the mode selector, which sends the boat's mode as the owner picks it.
// - Nothing on home is editable: the board is drawn read-only (HomeBoard).
// - On a phone (768 px and under) the Neomaris mark leads, beside the menu; the headline takes its own row under the board's name, wrapped
//   rather than cut. The bottom bar stays under home (ADR 0396 §5).
// - Home's headline carries the time Neo wrote it; Neo's status under it in
//   the fixed header goes on with the body and the thing to watch, and the
//   owner can dismiss it until Neo writes something else (TOM-683).
//
// - Home has no Ask Neo bar at any width: the sparkle at the bottom right, as
//   on every other boat page and on the iPad (TOM-808, TOM-817). The mode's
//   suggested questions, the mic and Talk are in the chat it opens, above and
//   in its message box, and a dot on the sparkle says Neo wrote while the chat
//   was closed. Neo's headline opens the same chat. The board runs to the foot
//   of home, its end scrolling clear of the sparkle.
// - Wider than 768 px the sparkle is the shell's: it slides the shell's chat
//   in over home (TOM-738, SlideChat), and nothing on home moves while it is in.
// - Wider than 768 px the shell's chat is docked on home instead (TOM-920,
//   cloud ADR 0451): a panel over the right side of the board, its top at the
//   board's top edge (--home-chat-top, set here from the header), so the
//   header and the banner stay clear. While it is open Neo's status body is
//   not drawn under the header: Neo's greeting in the chat says it. Leaving
//   home and coming back finds the chat as it was, and the board scrolled
//   where it was.
// - At 768 px and under the sparkle is home's own, as on Alarms and the board
//   pages (SparkleChat): Neo's main chat (TOM-511, NeoHomeChat) swoops in over
//   the whole screen, and home remembers it open or closed per boat.
//
// Neo's status and the alarm banner (TOM-512) fill the slots marked below.

import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { useBoatChrome, useHomeChrome } from '../components/app/boatChrome';
import { NeoHeadline } from '../components/app/NeoHeadline';
import { boatPath } from '../components/app/shellNav';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { HomeAlarmBanner } from '../components/home/HomeAlarmBanner';
import { NeoStatusBlock } from '../components/home/NeoStatusBlock';
import { NeoHomeChat } from '../components/NeoChat';
import { NeoSparkle } from '../components/neo/SparkleChat';
import { AppMark, Icon, InstrumentIcon } from '../components/ui/Icon';
import sceneLarge from '../assets/marina-scene-2172.webp';
import sceneSmall from '../assets/marina-scene-1086.webp';
import { BentoAreaContext, type BentoArea } from '../lib/bentoViability';
import { boatPhotoUrl } from '../lib/boatPhoto';
import { homeBoard, type HomeMode } from '../lib/homeBoard';
import { readDismissedStatus, statusWords, writeDismissedStatus } from '../lib/homeStatusDismiss';
import {
  readHomeChatOpen,
  readHomeScroll,
  writeHomeChatOpen,
  writeHomeScroll,
} from '../lib/homeChat';
import { askNeoSuggestions, boatClock, headerWeather, modeSinceLabel } from '../lib/homeHeader';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useForecast } from '../lib/useForecast';
import { PHONE_QUERY, useMediaQuery } from '../lib/useMediaQuery';
import { useModeSince } from '../lib/useModeSince';
import { useUnits } from '../lib/useUnits';

// The board, loaded with the board page's code.
const HomeBoardView = lazy(() => import('./Board').then((m) => ({ default: m.HomeBoard })));

/** How long to wait for the boat's mode before taking it as unknown (as ModeBoardRedirect waits). */
const MODE_WAIT_MS = 2_500;

/** The header's clock and its "since" line move on once a minute. */
const CLOCK_MS = 60_000;

const PRESENCE_TONE: Readonly<Record<string, string>> = {
  live: '',
  online: '',
  connecting: ' tb-status--warn',
  offline: ' tb-status--muted',
};

export function BoatHome() {
  const { boatId, boat, boards, archetype } = useBoatChrome();
  const chrome = useHomeChrome();
  const [params] = useSearchParams();
  const phone = useMediaQuery(PHONE_QUERY);
  const units = useUnits();
  const boatName = boat?.display_name ?? 'Your boat';
  useDocumentTitle(boat ? `${boat.display_name} — Home` : 'Home — Neomaris');

  // The boat's own photo, in place of the marina scene (TOM-683, ADR 0397). One
  // that fails to load gives the scene back.
  const photo = boat?.photo ?? null;
  const [failedPhoto, setFailedPhoto] = useState<string | null>(null);
  const listedPhotoUrl = boatPhotoUrl(boatId, photo);
  const photoUrl =
    listedPhotoUrl !== null && listedPhotoUrl !== failedPhoto ? listedPhotoUrl : null;

  const [waited, setWaited] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setWaited(true), MODE_WAIT_MS);
    return () => window.clearTimeout(id);
  }, []);

  const mode = useMemo<HomeMode>(
    () => ({ archetype: chrome.boatState?.state ?? null, anchorWatchOn: chrome.anchorWatchOn }),
    [chrome.boatState?.state, chrome.anchorWatchOn]
  );

  const listed = boards.state.kind === 'ready' || boards.state.kind === 'error';
  const systemBoards = boards.state.kind === 'ready' ? boards.state.systemBoards : [];
  const choosing = !listed || (archetype === undefined && !waited);
  const board = choosing ? null : homeBoard(systemBoards, mode);

  // Home's board area, for At anchor's bento (bentoViability).
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  // Coming back to home finds the board scrolled where it was (TOM-920): kept
  // as it scrolls, put back once the board has drawn tall enough.
  useEffect(() => {
    if (scrollEl === null) return;
    const target = readHomeScroll(boatId);
    let tries = 0;
    let frame = 0;
    const restore = () => {
      if (target <= 0) return;
      scrollEl.scrollTop = target;
      if (Math.abs(scrollEl.scrollTop - target) > 1 && tries++ < 30) {
        frame = requestAnimationFrame(restore);
      }
    };
    restore();
    const onScroll = () => writeHomeScroll(boatId, scrollEl.scrollTop);
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      scrollEl.removeEventListener('scroll', onScroll);
    };
  }, [scrollEl, boatId]);
  const [boardEl, setBoardEl] = useState<HTMLDivElement | null>(null);
  const area = useMemo<BentoArea | null>(
    () => (scrollEl !== null && boardEl !== null ? { scroll: scrollEl, board: boardEl } : null),
    [scrollEl, boardEl]
  );

  // Neo's status (TOM-512), read by the shell for every header's headline.
  const status = chrome.status.kind === 'ready' ? chrome.status.status : null;
  // The owner can dismiss its body until Neo writes something else (TOM-683).
  const [dismissedWords, setDismissedWords] = useState(() => readDismissedStatus(boatId));
  useEffect(() => setDismissedWords(readDismissedStatus(boatId)), [boatId]);
  const shownStatus = status !== null && statusWords(status) !== dismissedWords ? status : null;
  const dismissStatus = useCallback(() => {
    if (status === null) return;
    const words = statusWords(status);
    writeDismissedStatus(boatId, words);
    setDismissedWords(words);
  }, [boatId, status]);
  const boatOffline = chrome.presence !== 'live' && chrome.presence !== 'online';
  // Docked over the board and open (TOM-920): the greeting says the status.
  const dockOpen = chrome.homeChat.docked && chrome.homeChat.open;

  // The docked chat's top: the header's foot, kept as the header changes height.
  const headRef = useRef<HTMLElement | null>(null);
  const docked = chrome.homeChat.docked;
  useEffect(() => {
    const head = headRef.current;
    if (!docked || head === null) return;
    const root = document.documentElement;
    const place = () => {
      root.style.setProperty(
        '--home-chat-top',
        `${Math.round(head.getBoundingClientRect().bottom + 4)}px`
      );
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(head);
    window.addEventListener('resize', place);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
      root.style.removeProperty('--home-chat-top');
    };
  }, [docked]);

  // Where the board starts on the scene, for the scene's blurred copy under it
  // (home.css, TOM-925). Measured, since the header's height follows what it holds.
  const paneRef = useRef<HTMLElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const pane = paneRef.current;
    const head = headRef.current;
    const body = bodyRef.current;
    if (pane === null || head === null || body === null) return;
    const place = () => pane.style.setProperty('--home-board-top', `${body.offsetTop}px`);
    place();
    const observer = new ResizeObserver(place);
    observer.observe(head);
    return () => observer.disconnect();
  }, []);

  const title = board?.title ?? (choosing ? 'Home' : 'Boards');
  const presenceTone = PRESENCE_TONE[chrome.presence] ?? '';
  const modeLabel = chrome.boatState?.mode ?? 'not reported';

  // The header's clock, on the boat's time zone (Neo's status carries it).
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), CLOCK_MS);
    return () => window.clearInterval(id);
  }, []);
  const timeZone = status?.timeZone ?? null;
  const clock = boatClock(nowMs, timeZone);
  // Since when the boat has been in its mode: its mode history's answer, or no line.
  const sinceAt = useModeSince(
    boatId,
    chrome.boatState?.mode ?? null,
    chrome.boatState?.modeSince ?? ''
  );
  const since = sinceAt === null ? null : modeSinceLabel(sinceAt, nowMs, timeZone);
  // The weather at the boat, when it has a forecast; nothing otherwise.
  const forecast = useForecast(boatId, boatId !== '');
  const weather = headerWeather(forecast.data, nowMs, units);
  const suggestions = askNeoSuggestions(mode.archetype, mode.anchorWatchOn);

  // The board's name opens the mode selector (TOM-526), which gives the focus
  // back to it.
  const titleRef = useRef<HTMLButtonElement | null>(null);

  // Neo's chat on a phone: closed on a first visit, then as the owner left it
  // (TOM-520). Opening clears the sparkle's dot; closing gives the focus back
  // to the sparkle, which comes back as the chat swoops off (TOM-817).
  const slide = chrome.slideChat;
  const [homeChatOpen, setChatOpen] = useState(() => readHomeChatOpen(boatId));
  const [unread, setUnread] = useState(false);
  // Over home on a desktop (TOM-738, TOM-808), the shell's chat and its
  // sparkle: home stays as it is.
  const chatOpen = !slide && homeChatOpen;
  const sparkleRef = useRef<HTMLButtonElement | null>(null);
  const closedHere = useRef(false);
  const openChat = useCallback(
    (open: boolean) => {
      if (slide) {
        if (open) window.dispatchEvent(new CustomEvent('nm:open-neo'));
        return;
      }
      setChatOpen(open);
      writeHomeChatOpen(boatId, open);
      if (open) setUnread(false);
      else closedHere.current = true;
    },
    [boatId, slide]
  );
  const markUnread = useCallback(() => setUnread(true), []);
  useEffect(() => {
    setChatOpen(readHomeChatOpen(boatId));
    setUnread(false);
  }, [boatId]);
  useEffect(() => {
    if (chatOpen || !closedHere.current) return;
    closedHere.current = false;
    sparkleRef.current?.focus();
  }, [chatOpen]);

  // New chat linked the index with `?new=1` before the chat moved to `neo`.
  if (params.get('new') === '1') {
    return <Navigate to={`${boatPath(boatId, 'neo')}?${params.toString()}`} replace />;
  }

  const headline = <NeoHeadline status={status} onOpen={() => openChat(true)} home />;

  return (
    <div className="boat-home" data-testid="boat-home" data-chat={chatOpen ? 'open' : 'closed'}>
      {/* Under the full-screen chat on a phone, home is out of reach. */}
      <section
        ref={paneRef}
        className={`boat-home__pane${photoUrl !== null ? ' boat-home__pane--photo' : ''}`}
        aria-label={`${boatName} home`}
        inert={phone && chatOpen}
      >
        {/* The marina scene: decorative, behind the header and the top of the
            board, under a scrim. Or the boat's own photo across the same
            banner, the header as tall as its band. Night has neither (home.css). */}
        {photoUrl !== null && photo !== null ? (
          <div className="boat-home__scene boat-home__scene--photo" aria-hidden="true">
            <img
              src={photoUrl}
              width={photo.width}
              height={photo.height}
              alt=""
              decoding="async"
              data-testid="home-photo"
              onError={() => setFailedPhoto(photoUrl)}
            />
            <img
              className="boat-home__scene-blur"
              src={photoUrl}
              width={photo.width}
              height={photo.height}
              alt=""
              decoding="async"
            />
          </div>
        ) : (
          <div className="boat-home__scene" aria-hidden="true">
            <img
              src={sceneLarge}
              srcSet={`${sceneSmall} 1086w, ${sceneLarge} 2172w`}
              sizes="100vw"
              alt=""
              decoding="async"
              data-testid="home-scene"
            />
            <img
              className="boat-home__scene-blur"
              src={sceneLarge}
              srcSet={`${sceneSmall} 1086w, ${sceneLarge} 2172w`}
              sizes="100vw"
              alt=""
              decoding="async"
            />
          </div>
        )}
        <header ref={headRef} className="boat-home__head">
          {phone ? (
            <div className="boat-home__brand">
              <img className="boat-home__logo" src="/logo.png" alt="" width={30} height={30} />
              <span className="boat-home__night-mark">
                <AppMark size={30} />
              </span>
              <span className="boat-home__wordmark">Neomaris</span>
            </div>
          ) : null}

          <div className="boat-home__titles">
            <p className="boat-home__boat">
              <span className="boat-home__boat-name">{boatName}</span>
              <span className={`tb-status${presenceTone}`} data-testid="home-presence">
                {chrome.presenceLabel}
              </span>
            </p>
            <h1 className="boat-home__title">
              <button
                ref={titleRef}
                type="button"
                className="boat-home__board-name"
                aria-haspopup="menu"
                aria-expanded={
                  chrome.modeOpenFrom !== null && chrome.modeOpenFrom === titleRef.current
                }
                aria-label={`${title}. Boat mode: ${modeLabel}. Change`}
                data-testid="home-board-name"
                disabled={!listed}
                onClick={(e) => chrome.openMode(e.currentTarget, 'start')}
              >
                <span>{title}</span>
                <Icon name="chevron-down" size={24} strokeWidth={2.4} />
              </button>
            </h1>
            {since !== null ? (
              <p className="boat-home__since" data-testid="home-mode-since">
                {since}
              </p>
            ) : null}
          </div>

          <div className="boat-home__aside">
            {phone ? null : <div className="boat-home__headline">{headline}</div>}
            {phone || weather === null ? null : (
              <p
                className="boat-home__tile boat-home__weather"
                title="Forecast at the boat's position"
                data-testid="home-weather"
              >
                <InstrumentIcon name={weather.icon} size={34} />
                <span className="boat-home__tile-text">
                  <strong>
                    {weather.temperature}
                    {weather.unit}
                  </strong>
                  <span>{weather.sky}</span>
                  {weather.wind !== null ? <span>{weather.wind}</span> : null}
                </span>
              </p>
            )}
            {phone ? null : (
              <p className="boat-home__tile boat-home__clock" data-testid="home-clock">
                <span>{clock.date}</span>
                <strong>{clock.time}</strong>
              </p>
            )}
            {phone ? (
              <button
                type="button"
                className="tb-icon-btn tb-icon-btn--glass boat-home__menu"
                aria-label="Menu"
                aria-haspopup="dialog"
                aria-expanded={chrome.menuOpen}
                data-testid="boat-menu-button"
                onClick={chrome.openMenu}
              >
                <Icon name="menu" size={20} />
              </button>
            ) : null}
          </div>

          {phone ? <div className="boat-home__headline">{headline}</div> : null}
          {/* The alarm banner's slot (TOM-512), pinned in the fixed top, on its own row
              under the board's name while an alarm needs attention. Empty, it takes no room. */}
          <div className="boat-home__alarm" data-slot="alarm">
            <HomeAlarmBanner boatOffline={boatOffline} />
          </div>
          {/* Neo's status's slot (TOM-512): its body and its watch item, the headline
              being above (TOM-526). In the fixed header since TOM-683, so scrolling the
              board never moves it, and the owner can dismiss it. Empty, it takes no room. */}
          <div className="boat-home__status" data-slot="status">
            {shownStatus !== null && !dockOpen ? (
              <NeoStatusBlock status={shownStatus} onDismiss={dismissStatus} />
            ) : null}
          </div>
        </header>

        {/* The board area, over the scene blurred once for the glass of Dark's
            cards (home.css): never a blur on each card. */}
        <div ref={bodyRef} className="boat-home__body">
          <div ref={setScrollEl} className="boat-home__scroll" data-testid="home-scroll">
            <div ref={setBoardEl} className="boat-home__board neo-shell--dashboard">
              {choosing ? (
                <p className="muted boat-home__loading" aria-busy="true">
                  Loading your boards…
                </p>
              ) : board === null ? (
                <div className="tb-card board-empty" data-testid="boards-waiting">
                  <div className="tb-empty">
                    <h3 className="tb-empty__title">Setting up {boatName}’s boards</h3>
                    <p className="tb-subhead">
                      Waiting for the first readings from {boatName}. Its system boards appear as
                      soon as it reports.
                    </p>
                  </div>
                </div>
              ) : (
                <BentoAreaContext.Provider value={area}>
                  <Suspense
                    fallback={<p className="muted boat-home__loading">Loading the board…</p>}
                  >
                    <HomeBoardView key={board.slug} slug={board.slug} />
                  </Suspense>
                </BentoAreaContext.Provider>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* Neo's main chat on a phone (TOM-511, TOM-520): over the whole screen,
          when open. A failure in it leaves the board standing. */}
      {slide ? null : (
        <ErrorBoundary label="home chat">
          <NeoHomeChat
            key={boatId}
            boatId={boatId}
            open={chatOpen}
            fullScreen={phone}
            onOpenChange={openChat}
            onUnread={markUnread}
            suggestions={suggestions}
          />
        </ErrorBoundary>
      )}
      {/* Closed, the sparkle at the bottom right opens it, placed as on every
          other page (sparkle-chat.css, TOM-817). */}
      {slide || chatOpen ? null : (
        <div className="sparkle-chat">
          <NeoSparkle buttonRef={sparkleRef} unread={unread} onOpen={() => openChat(true)} />
        </div>
      )}
    </div>
  );
}
